import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import { FunctionComponent, memo, useCallback } from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import ChangesDelayedWarning from '../ChangesDelayedWarning/ChangesDelayedWarning';
import { useResetDeviceTokenMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: theme.spacing(50),
  },
}));

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

const ResetTokenDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const classes = useStyles();
    const config = useConfig();
    const { authUser } = useAuthUser();
    const [resetToken, { isLoading }] = useResetDeviceTokenMutation();
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    return (
      <>
        <Dialog
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Reset Token</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <ChangesDelayedWarning />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              onClick={async () => {
                const resp: any = await resetToken({
                  apiBaseURL: config.apiBaseURL,
                  orgIDs: authUser.selectedOrgs,
                  devices,
                });
                if (!resp.error) {
                  handleClose();
                  handleCreated();
                  return;
                }
                enqueueSnackbar(parseErrorMessage(resp.error), {
                  variant: 'error',
                });
              }}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ResetTokenDialog;
